import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { MetricCard } from "./MetricCard";
import { SkeletonGrid } from "./common";

/* ======================================================================== */
/* TAB 3 — MÉTRICAS                                                          */
/* ======================================================================== */
export function MetricsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/landing/metrics");
      setItems(data.items || data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudieron cargar métricas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleVisible = async (m) => {
    try {
      await api.patch(`/admin/landing/metrics/${m.key}`, {
        is_visible: !m.is_visible,
        custom_label: m.effective_label !== m.default_label ? m.effective_label : null,
        custom_value_override: m.effective_value !== m.computed_value ? m.effective_value : null,
        sort_order: m.sort_order,
      });
      toast.success(m.is_visible ? "Métrica ocultada" : "Métrica visible en landing");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar");
    }
  };

  return (
    <div className="space-y-4" data-testid="metrics-tab">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="font-serif text-lg text-gray-900">Métricas</h2>
          <p className="text-sm text-gray-600">
            Activa las métricas que se mostrarán en el landing. Los valores se calculan en vivo desde la base de datos.
            Puedes sobreescribir el valor o la etiqueta si lo necesitas.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-sm border-gray-300" data-testid="btn-reload-metrics">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Recalcular
        </Button>
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : (
        <ul className="grid sm:grid-cols-2 gap-4" data-testid="metrics-list">
          {items.map((m) => (
            <li key={m.key} data-testid={`metric-${m.key}`}>
              <MetricCard
                metric={m}
                editing={editingKey === m.key}
                onEdit={() => setEditingKey(m.key)}
                onCloseEdit={() => setEditingKey(null)}
                onSaved={load}
                onToggleVisible={() => toggleVisible(m)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
